/**
 * Platform sniffing, confined to one file.
 *
 * The awkward truth this app has to work around: on iOS, web push only exists
 * once the site has been added to the home screen (iOS 16.4+), and there is no
 * install prompt — the user has to go through the Share sheet by hand. So the
 * onboarding has to detect "iOS Safari, not yet installed" and explain it.
 */

export function isStandalone(): boolean {
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    // iOS reports standalone through a non-standard navigator flag.
    (window.navigator as Navigator & { standalone?: boolean }).standalone === true
  );
}

export function isIos(): boolean {
  const ua = window.navigator.userAgent;
  return (
    /iPad|iPhone|iPod/.test(ua) ||
    // iPadOS 13+ reports itself as a Mac; the touch points give it away.
    (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1)
  );
}

export function isAndroid(): boolean {
  return /Android/.test(window.navigator.userAgent);
}

export function supportsPush(): boolean {
  return "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
}

export type PushReadiness =
  | { state: "ready" }                 // we can ask for permission right now
  | { state: "needs-install" }         // iOS, still in Safari — install first
  | { state: "unsupported" }           // no push on this browser at all
  | { state: "denied" }                // the user said no; only settings can undo it
  | { state: "granted" };              // already on

export function pushReadiness(): PushReadiness {
  if (!supportsPush()) {
    // On iOS the APIs simply are not there until the app is installed, so
    // "unsupported" here usually means "not installed yet".
    return isIos() && !isStandalone() ? { state: "needs-install" } : { state: "unsupported" };
  }
  if (isIos() && !isStandalone()) return { state: "needs-install" };
  if (Notification.permission === "granted") return { state: "granted" };
  if (Notification.permission === "denied") return { state: "denied" };
  return { state: "ready" };
}
